import React from "react";
import ReactSelect, {
  components as RSComponents,
  OptionProps,
} from "react-select";
import cn from "classnames";
import { Label } from "components";
import { useTranslation } from "react-i18next";

export type PrefixOption = { id: number; label: string; value: string };

type Props = {
  value: string;
  options: PrefixOption[];
  onChange: (value: string) => void;
  isDisabled?: boolean;
  isRequired?: boolean;
  label?: string;
  error?: string | boolean;
  name?: string;
  id?: string;
};

const toRSOptions = (options: PrefixOption[]) =>
  options.map((option) => ({ label: option.label, value: option.value }));

const PrefixSelect: React.FC<Props> = ({
  value,
  options,
  onChange,
  isDisabled,
  isRequired,
  label,
  error,
  name = "prefix",
  id = "prefix",
}) => {
  const { t } = useTranslation();
  const rsOptions = toRSOptions(options);
  const selected =
    value !== undefined && value !== null
      ? {
          label:
            rsOptions.find((option) => option.value === value)?.label || value,
          value,
        }
      : value === ""
        ? {
            label: rsOptions.find((option) => option.value === "")?.label || "",
            value: "",
          }
        : null;

  const Option = (props: OptionProps<any, false>) => {
    const { isSelected, isFocused } = props;
    return (
      <RSComponents.Option {...props} className={undefined}>
        <div
          className={cn(
            "flex items-center justify-between px-3 py-2",
            "-bg--theme-colors-surface-neutral-level02 -text--component-colors-field-default-input-text",
            "hover:-bg--theme-colors-surface-neutral-level03 hover:-text--component-colors-field-hover-input-text",
            {
              "-bg--theme-colors-surface-neutral-level03 -text--component-colors-field-hover-input-text":
                isSelected || isFocused,
            },
          )}
        >
          <span>{(props.data as any).label}</span>
          <span className="ms-3">{isSelected ? "\u2713" : null}</span>
        </div>
      </RSComponents.Option>
    );
  };

  return (
    <div className="w-full">
      {label && (
        <Label htmlFor={id} error={!!error} required={isRequired}>
          {label}
        </Label>
      )}
      <ReactSelect
        inputId={id}
        name={name}
        value={selected}
        onChange={(opt) => onChange(opt ? (opt as any).value : "")}
        options={rsOptions}
        isDisabled={isDisabled}
        classNamePrefix="rs"
        unstyled
        placeholder={t("common.labels.select")}
        classNames={{
          container: () => "w-full mt-2.5",
          control: (state) =>
            cn(
              "!min-h-[48px] !bg-inherit !border appearance-none",
              "!outline-none focus:!outline-none !shadow-none focus:!shadow-none focus:!ring-0",
              "-rounded-input",
              {
                "-border--component-colors-field-error-input-border -text--component-colors-field-error-input-text":
                  !!error,
                "-border--component-colors-field-selected-input-border":
                  !error && state.isFocused,
                "-border--component-colors-field-default-input-border":
                  !error && !state.isFocused,
              },
            ),
          valueContainer: () => "!py-2.5 !px-3",
          singleValue: () => "-text--component-colors-field-filled-input-text",
          input: () => "-text--component-colors-field-filled-input-text",
          placeholder: () =>
            "-text--component-colors-field-default-input-placeholder",
          menu: () =>
            "-bg--theme-colors-surface-neutral-level02 !z-50 border -border--component-colors-field-default-input-border",
          menuList: () => "py-0 max-h-64 overflow-auto",
          option: () => "px-0 py-0",
          indicatorsContainer: () => "pe-2",
          dropdownIndicator: () =>
            "-text--component-colors-field-default-input-text me-1.5",
          indicatorSeparator: () => "!hidden",
        }}
        isSearchable
        components={{ Option }}
      />
      {error ? (
        <div className="text-sm text-red-600 mt-1">
          {typeof error === "string" ? error : "This field is required"}
        </div>
      ) : null}
    </div>
  );
};

export default PrefixSelect;
